import React from 'react'
import { Grid } from '@mui/material'

import useTemperatures from '../../../utils/hooks/useTemperatures'
import useSharedStyles from '../../../utils/shared.styles'

import BinaryChart from './charts/binary'
import LineChart from './charts/line'

const Chart = ({
  chart,
  deviceId,
  currentValues,
  createdAt,
  alerts,
  nodeType,
}: {
  chart: string
  deviceId: number
  currentValues: ICurrentValueType
  createdAt: string
  alerts: IAlertHistory[]
  nodeType: string
}) => {
  const { tempUnits } = useTemperatures()
  const sharedClasses = useSharedStyles
  const channels = []

  let includeSignalQuality = false
  if (chart === 'signal' && currentValues.cellrssi?.value !== undefined)
    channels.push({
      channelName: 'cellrssi',
      yAxisTitle: 'Signal Strength (Cell RSSI)',
      seriesName: 'Cell RSSI',
      unit: 'dBm',
    })
  if (chart === 'signal' && currentValues.wifirssi?.value !== undefined)
    channels.push({
      channelName: 'wifirssi',
      yAxisTitle: 'Signal Strength (WiFi RSSI)',
      seriesName: 'WiFi RSSI',
      unit: 'dBm',
    })
  if (chart === 'signal' && currentValues.cellrsrq?.value !== undefined)
    channels.push({
      channelName: 'cellrsrq',
      yAxisTitle: 'Signal Strength (Cell RSRP)',
      seriesName: 'Cell RSRP',
      unit: 'dBm',
    })
  if (chart === 'signal' && currentValues.rssi?.value !== undefined)
    channels.push({
      channelName: 'rssi',
      yAxisTitle: 'Signal Strength (RSSI)',
      seriesName: 'RSSI',
      unit: 'dBm',
    })
  if (chart === 'signal' && currentValues.relay_rssi?.value !== undefined)
    channels.push({
      channelName: 'relay_rssi',
      yAxisTitle: 'Signal Strength (RSSI Relay)',
      seriesName: 'RSSI Relay',
      unit: 'dBm',
    })
  if (chart === 'signal' && currentValues.snr?.value !== undefined && nodeType !== 'kerlink_wifi_gateway') {
    includeSignalQuality = true
    channels.push({
      channelName: 'snr',
      yAxisTitle: 'Signal Quality (SNR)',
      seriesName: 'SNR',
      unit: 'dB',
    })
  }

  if (chart === 'temp' && currentValues.temp)
    channels.push({
      channelName: 'temp',
      yAxisTitle: `Temperature (deg ${tempUnits})`,
      seriesName: 'Temperature',
      unit: `°${tempUnits}`,
    })
  if (chart === 'temp' && currentValues.probe)
    channels.push({
      channelName: 'probe',
      yAxisTitle: `Probe (deg ${tempUnits})`,
      seriesName: 'Probe',
      unit: `°${tempUnits}`,
    })
  if (chart === 'temp' && currentValues.probe_temp)
    channels.push({
      channelName: 'probe_temp',
      yAxisTitle: `Probe (deg ${tempUnits})`,
      seriesName: 'Probe',
      unit: `°${tempUnits}`,
    })
  if (chart === 'temp' && currentValues.hum)
    channels.push({
      channelName: 'hum',
      yAxisTitle: 'Humidity (%)',
      seriesName: 'Humidity',
      unit: '%',
    })
  if (chart === 'temp' && currentValues.hum)
    channels.push({
      channelName: 'outsidetemp',
      yAxisTitle: `Outside Temperature (deg ${tempUnits})`,
      seriesName: 'Outside Temperature',
      unit: `°${tempUnits}`,
    })
  if (chart === 'battery' && currentValues.battery)
    channels.push({
      channelName: 'battery',
      yAxisTitle: 'Battery (%)',
      seriesName: 'Battery',
      unit: '%',
    })
  if (chart === 'leak' && currentValues.leak)
    channels.push({
      channelName: 'leak',
      yAxisTitle: '',
      seriesName: 'Leak',
      unit: '',
    })
  if (chart === 'leakprobe' && currentValues.leak_probe)
    channels.push({
      channelName: 'leak_probe',
      yAxisTitle: '',
      seriesName: 'Leak Probe',
      unit: '',
    })
  if (chart === 'valve' && currentValues.valve)
    channels.push({
      channelName: 'valve',
      yAxisTitle: '',
      seriesName: 'Valve Closed',
      unit: '',
    })
  if (chart === 'ammonia' && currentValues.nh3)
    channels.push({
      channelName: 'nh3',
      yAxisTitle: 'Ammonia (ppm)',
      seriesName: 'Ammonia',
      unit: 'ppm',
    })
  if (chart === 'ammonia_warning' && currentValues.warning_nh3)
    channels.push({
      channelName: 'warning_nh3',
      yAxisTitle: '',
      seriesName: 'Ammonia Warning',
      unit: '',
    })
  if (chart === 'powersource' && currentValues.powersource)
    channels.push({
      channelName: 'powersource',
      yAxisTitle: '',
      seriesName: 'Power Source',
      unit: '',
    })

  if (channels.length > 0)
    return (
      <Grid container={true} data-testid={`chart-${chart}`} id={`chart_${chart}`} size={12} sx={sharedClasses.centerContentHorizontal}>
        {chart === 'signal' && (
          <LineChart
            title={includeSignalQuality ? 'Signal & Signal Quality' : 'Signal'}
            deviceId={deviceId}
            channels={channels}
            createdAt={createdAt}
            alerts={alerts}
            isSignalChart={true}
            chart={chart}
          />
        )}
        {chart === 'battery' && <LineChart title="Battery" deviceId={deviceId} channels={channels} createdAt={createdAt} alerts={alerts} chart={chart} />}
        {chart === 'ammonia' && <LineChart title="Ammonia" deviceId={deviceId} channels={channels} createdAt={createdAt} alerts={alerts} chart={chart} />}
        {chart === 'temp' && (
          <LineChart
            title={currentValues.temp && currentValues.hum ? 'Temperature & Humidity' : 'Temperature'}
            deviceId={deviceId}
            channels={channels}
            createdAt={createdAt}
            alerts={alerts}
            chart={chart}
          />
        )}
        {(chart === 'leak' || chart === 'leakprobe') && (
          <BinaryChart title="Leak" deviceId={deviceId} channel={channels[0]} createdAt={createdAt} alerts={alerts} chart={chart} />
        )}
        {chart === 'valve' && (
          <BinaryChart title="Valve Position" deviceId={deviceId} channel={channels[0]} createdAt={createdAt} alerts={alerts} chart={chart} />
        )}
        {chart === 'ammonia_warning' && (
          <BinaryChart title="Ammonia Warning" deviceId={deviceId} channel={channels[0]} createdAt={createdAt} alerts={alerts} chart={chart} />
        )}
        {chart === 'powersource' && (
          <BinaryChart title="Power Source" deviceId={deviceId} channel={channels[0]} createdAt={createdAt} alerts={alerts} chart={chart} />
        )}
      </Grid>
    )
  return null
}

const DeviceCharts = ({
  deviceId,
  currentValues,
  createdAt,
  alerts,
  nodeType,
}: {
  deviceId: number
  currentValues: ICurrentValueType
  createdAt: string
  alerts: IAlertHistory[]
  nodeType: string
}) => {
  const charts = []
  if (
    currentValues.rssi ||
    currentValues.snr ||
    currentValues.rsrp ||
    currentValues.cellrssi ||
    currentValues.cellrsrq ||
    currentValues.wifirssi ||
    currentValues.relay_rssi
  )
    charts.push('signal')
  if (currentValues.battery) charts.push('battery')
  if ((currentValues.temp || currentValues.probe || currentValues.probe_temp || currentValues.hum) && nodeType !== 'bvs2') charts.push('temp')
  if (currentValues.leak) charts.push('leak')
  if (currentValues.valve) charts.push('valve')
  if (currentValues.leak_probe) charts.push('leakprobe')
  if (currentValues.nh3) charts.push('ammonia')
  if (currentValues.warning_nh3) charts.push('ammonia_warning')
  if (currentValues.powersource && nodeType !== 'bvs' && nodeType !== 'bvs2') charts.push('powersource')

  return (
    <Grid
      container={true}
      data-testid="device-charts"
      sx={{
        my: 4,
        gap: 4,
      }}
      className="highcharts-light"
    >
      {charts.map((chart) => (
        <Chart key={chart} chart={chart} deviceId={deviceId} currentValues={currentValues} createdAt={createdAt} alerts={alerts} nodeType={nodeType} />
      ))}
    </Grid>
  )
}

export default React.memo(DeviceCharts)
